'use client';

import { API_ROUTES, useApiInvalidate, useApiMutation, useApiQuery } from '@shared/api-utils';
import { useSearchParams } from 'next/navigation';

type ContactPreferencesResponse = { verticals: Record<string, boolean>; notificationChannels: Record<string, boolean> };

/**
 * Hook to manage contact preferences for the unsubscribe page (API doesn't require authentication)
 * @returns An object containing:
 * - contact preferences data
 * - loading state
 * - error
 * - update preferences mutation function
 * - mutation error
 * - mutation pending state
 * - mutation success state
 * - mutation reset function
 */
function useUnsubscribe() {
  const searchParams = useSearchParams();
  const unsubscribeId = searchParams.get('p');
  const apiUrl = `${API_ROUTES.UNSUBSCRIBE}?unsubscribeId=${unsubscribeId ?? ''}`;
  const { invalidateQuery } = useApiInvalidate();
  const { data, isLoading, error } = useApiQuery<ContactPreferencesResponse>(apiUrl);
  const {
    mutateAsync: updatePreferences,
    error: mutationError,
    isPending,
    isSuccess,
    reset,
  } = useApiMutation<ContactPreferencesResponse, ContactPreferencesResponse>(apiUrl, 'PUT', {
    mutationOptions: {
      onSettled: () => {
        invalidateQuery(apiUrl);
        invalidateQuery(API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET);
      },
    },
    minimumDelayMs: 1000,
  });

  return {
    data,
    isLoading,
    error,
    updatePreferences,
    mutationError,
    isPending,
    isSuccess,
    reset,
  };
}

export { useUnsubscribe };
